<template>
  <div id="agWfPage">
    <!-- loading spinner, no workspaces, or no workflows -->
    <b-spinner v-if="isLoading" id="agWfPage-loadingSpinner"></b-spinner>
    <div id="agWfPage-noWss" v-else-if="workspaces.length == 0">
      <fa icon="briefcase" />
      <div>No workspaces have been created</div>
    </div>
    <div id="agWfPage-noWfs" v-else-if="workflows.length == 0">
      <fa icon="project-diagram" />
      <div>No workflows have been created</div>
    </div>

    <!-- workflow modal -->
    <b-modal
      ref="workflowModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      size="lg">
      <template slot="modal-header">
        <h4 v-if="isReadonly" class="agModal-title">View Workflow</h4>
        <h4 v-else-if="modalWorkflow._id" class="agModal-title">Edit Workflow</h4>
        <h4 v-else class="agModal-title">New Workflow</h4>
      </template>
      <ag-workflow-metadata
        ref="workflowMetadata"
        :workflow="modalWorkflow"
        :isReadonly="isReadonly"
        :isLoading="isSaving || isDeleting"
        :showLaunchButton="true"
        @launchWorkflowEditor="saveThenLaunchWorkflowEditor">
      </ag-workflow-metadata>
       <template slot="modal-footer" slot-scope="{cancel}">
        <b-button class="agModal-buttonLeft" @click="cancel">Cancel</b-button>
        <b-spinner v-if="isLoading" class="agModal-loadingSpinner"></b-spinner>
        <b-button
          class="agModal-buttonRight"
          variant="primary"
          :disabled="isReadonly || isLoading"
          @click="saveWfButton_click"
        >Save</b-button>
      </template>
    </b-modal>

    <!-- import modal -->
    <ag-import-modal ref="importModal" @reloadWorkflows="reloadWorkflows"></ag-import-modal>

    <!-- export modal -->
    <ag-export-modal ref="exportModal" :workflow="modalWorkflow"></ag-export-modal>

    <!-- copy modal -->
    <ag-copy-modal ref="copyModal" :workflow="modalWorkflow" @reloadWorkflows="reloadWorkflows"></ag-copy-modal>

    <!-- delete modal -->
    <b-modal
      ref="deleteModal"
      id="agWfPage-deleteModal"
      class="agModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="deleteWfButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title danger">Delete Workflow</h4>
      </template>
      Are you sure you want to delete workflow
      <i>{{modalWorkflow.name}}</i>?
      <template slot="modal-footer" slot-scope="{cancel}">
        <b-button class="agModal-buttonLeft" @click="cancel">Cancel</b-button>
        <b-button class="agModal-buttonRight" variant="danger" @click="deleteWfButton_click">Delete</b-button>
      </template>
    </b-modal>

    <!-- readonly modal -->
    <b-modal
      v-if="editorWorkflow"
      class="agModal"
      ref="readonlyModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="confirmReadonlyButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title">Read-Only Workflow</h4>
      </template>
      Workflow
      <i>{{editorWorkflow.name}}</i>{{readonlyMessage}}
      <template
        slot="modal-footer">
        <b-button class="agModal-buttonLeft" @click="cancelReadonlyButton_click">Cancel</b-button>
        <b-button
          class="agModal-buttonRight"
          variant="primary"
          @click="confirmReadonlyButton_click"
        >View Workflow</b-button>
      </template>
    </b-modal>

    <!-- toolbar -->
    <ag-toolbar>
      <template slot="leftContent">
        <div class="agInputGroup">
        <b-dropdown
          class="agDropdown"
          no-caret
          dropright
          v-b-tooltip.hover
          title="Add workflow"
          :disabled="isReadonly || isLoading || workspaces.length == 0">
          <template slot="button-content">
            <fa icon="plus" />
          </template>
          <b-dropdown-item
            href="#"
            @click="addWfButton_click">
            <fa icon="plus-circle" />New
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="importWfButton_click">
            <fa icon="file-import" />Import
          </b-dropdown-item>
        </b-dropdown>
        <b-input v-model="searchString" type="text" autocomplete="false" placeholder="Search..."></b-input>
        </div>
      </template>
      <template slot="rightContent">
        <div class="agInputGroup">
          <b-spinner v-if="workflowLoading"></b-spinner>
          <label>Workspace:</label>
          <b-form-select
            v-model="workspace"
            :options="workspaceOptions"
            :disabled="isLoading || workspaces.length == 0"
          ></b-form-select>
        </div>
      </template>
    </ag-toolbar>

    <!-- workflows -->
    <b-card id="agWfPage-wfs" v-if="!isLoading && sortedWorkflows.length > 0" :key="reload">
      <draggable v-model="sortedWorkflows" handle=".card-header" ghost-class="agWf-ghost" :disabled="isSearching">
        <ag-workflow
          v-for="workflow in sortedWorkflows"
          :key="workflow._id"
          :workflow="workflow"
          @editWorkflow="editWorkflow"
          @copyWorkflow="copyWorkflow"
          @exportWorkflow="exportWorkflow"
          @deleteWorkflow="deleteWorkflow"
          @summaryWorkflow="summaryWorkflow"
          @launchWorkflowEditor="launchWorkflowEditor"
          @reloadWorkflows="reloadWorkflows"
        ></ag-workflow>
      </draggable>
    </b-card>
  </div>
</template>





<script>
// components
import Toolbar from "ui/Toolbar";
import Workflow from "workflow/Workflow";
import WorkflowMetadata from "workflow/WorkflowMetadata";
import CopyModal from "workflow/CopyModal";
import ExportModal from "workflow/ExportModal";
import ImportModal from "workflow/ImportModal";

// modules
import Server from "src/server";
import Util from "src/util/util";
import Draggable from "vuedraggable";
import Moment from "moment";
import { EventBus } from "src/eventBus";

export default {
  name: "ag-workflows-page",
  components: {
    "ag-toolbar": Toolbar,
    "ag-workflow": Workflow,
    "ag-workflow-metadata": WorkflowMetadata,
    "ag-copy-modal": CopyModal,
    "ag-export-modal": ExportModal,
    "ag-import-modal": ImportModal,
    draggable: Draggable
  },
  data() {
    return {
      reload: 0,
      isSaving: false,
      isDeleting: false,
      isSearching: false,
      workflowLoading: false,
      workspaceOptions: [],
      sortedWorkflows: [],
      modalWorkflow: {
        name: "",
        description: "",
        startDate: "",
        frequencyType: "",
        schedule: {
          days: [false, false, false, false, false, false, false],
          startDate: "",
          startTime: "",
          endTime: "",
          frequency: 60
        }
      },
      editorWorkflow: null,
      readonlyMessage: "",
      searchString: "",
    };
  },
  computed: {

    isLoading() {
      if (
        this.$store.state.wfStore.isLoading ||
        this.$store.state.wsStore.isLoading ||
        this.isSaving ||
        this.isDeleting
      )
        return true;
      return false;
    },

    isReadonly() {
      if (this.user.role == "readonly") return true;
        return false;
    },

    user() {
      return this.$store.state.userStore.user;
    },

    workspace: {
      get() {
        if(this.$store.state.wsStore.workspace)
          return this.$store.state.wsStore.workspace._id;
      },
      set(value) {
        this.$store.dispatch("wsStore/setWorkspace", {
          workspace: this.workspaces.find(a => a._id == value)
        });
      }
    },

    workspaces() {
      return this.$store.getters["wsStore/userWorkspaces"];
    },

    workflows() {
      return this.$store.state.wfStore.workflows;
    }
  },

  watch: {
    workspaces() {
      this.setWorkspaceOptions();
    },

    workflows() {
      this.setSortedWorkflows();
    },

    sortedWorkflows(oldList, newList) {
      if (
        !!newList &&
        !!oldList &&
        oldList.length > 0 &&
        newList.length > 0 &&
        newList.length === oldList.length &&
        JSON.stringify(oldList) != JSON.stringify(newList)
      )
        this.saveWorkflowOrder();
    },

    searchString() {
      this.setFilteredWorkflows();
    }
  },

  mounted() {
    this.setWorkspaceOptions();
    this.setSortedWorkflows();
  },

  methods: {
    // UI Events

    addWfButton_click() {
      this.resetModalWorkflow();
      this.$refs.workflowModal.show();
    },

    importWfButton_click() {
      this.$refs.importModal.show();
    },

    async deleteWfButton_click() {
      this.$refs.deleteModal.hide();
      this.isDeleting = true;
      await this.$store.dispatch("wfStore/deleteWorkflow", {
        workflow: this.modalWorkflow
      });
      this.isDeleting = false;
      this.resetModalWorkflow();
    },

    async confirmReadonlyButton_click() {
      this.$refs.readonlyModal.hide();
      this.$emit("setPage", "workflowEditor", {
        title: this.editorWorkflow.name,
        readonly: true
      });
    },

    cancelReadonlyButton_click() {
      this.$refs.readonlyModal.hide();
    },

    async saveWfButton_click() {

      if(!this.$refs.workflowMetadata.validate())
        return;

      if (this.modalWorkflow._id)
        var func = "updateWorkflow";
      else
        var func = "insertWorkflow";

      this.isSaving = true;
      var result = await this.$store.dispatch("wfStore/" + func, {
        workflow: this.modalWorkflow
      });
      this.isSaving = false;
      if (result.data.success) {
        this.$refs.workflowModal.hide();
        this.reloadWorkflows();
      } else {
        this.$refs.workflowMetadata.modalMessage = result.data.error;
        this.$refs.workflowMetadata.modalMessageClass = "agModal-errorMessage";
      }
      return result;
    },

    // emitted events

    editWorkflow(workflow) {
      this.modalWorkflow = JSON.parse(JSON.stringify(workflow));
      this.$refs.workflowModal.show();
    },

    copyWorkflow(workflow) {
      this.modalWorkflow = JSON.parse(JSON.stringify(workflow));
      this.$refs.copyModal.show();
    },

    exportWorkflow(workflow) {
      this.modalWorkflow = JSON.parse(JSON.stringify(workflow));
      this.$refs.exportModal.show();
    },

    deleteWorkflow(workflow) {
      this.modalWorkflow = JSON.parse(JSON.stringify(workflow));
      this.$refs.deleteModal.show();
    },

    async summaryWorkflow(workflow) {
      try {
        this.workflowLoading = true
        let sum;
        if (workflow.summary !== undefined) {
            sum = await Server.post("updateSummaryFromOpenai", {data: {workflow: workflow._id}}).promise;
        } else {
            sum = await Server.post("getSummaryFromOpenai", {data: {workflow: workflow._id}}).promise;
        }

        if (!sum.data.success) {
          EventBus.$emit("showAlert", {
              title: "Error",
              message: sum.data.error,
              type: "error"
          });
        }
        if (sum.data.success){
          var result = await this.$store.dispatch("wfStore/updateWorkflow", {
            workflow: sum.data.workflow
          });
          this.reloadWorkflows();
        }
        this.workflowLoading = false;
      } catch (error) {
          console.error("An error occurred:", error);
          EventBus.$emit("showAlert", {
              title: "Error",
              message: "An error occurred while processing the workflow",
              type: "error"
        });
      }
    },

    async saveThenLaunchWorkflowEditor() {
      var result = await this.saveWfButton_click();
      if (result.data.success)
        this.launchWorkflowEditor(result.data.workflow);
    },

    async launchWorkflowEditor(workflow) {

      // first set the workflow, which returns full workflow w/ latest data
      var result = await this.$store.dispatch("wfStore/setWorkflow", {
        workflow: JSON.parse(JSON.stringify(workflow))
      });
      if(!result.data.success || !result.data.workflow) {
        EventBus.$emit("showAlert", {
          title: "Failed to Load Workflow",
          message: "Please try again or contact your administrator",
          type: "error"
        });
        return;
      }
      workflow = result.data.workflow;

      // check to see if it is running
      var runCheck = await Server.get("isWorkflowRunning/" + workflow._id).promise;

      // if running, then readonly editor
      if (runCheck.data.running) {
        this.$emit("setPage", "workflowEditor", {
          title: workflow.name,
          running: true,
          readonly: true
        });
        return;
      }

      // if readonly user, then readonly editor
      if(this.isReadonly) {
        this.$emit("setPage", "workflowEditor", {
          title: workflow.name,
          readonly: true
        });
        return;
      }

      // check to see if it is locked by another user
      var lockCheck = await Server.post("isWorkflowLocked", {workflow: workflow._id}).promise;

      // if read/write user but locked, show readonly confirmation
      if(lockCheck.data.locked) {
        this.editorWorkflow = workflow;
        this.readonlyMessage = ` is currently locked by ${lockCheck.data.user} and will be launched in read-only mode.`;
        setTimeout(() => {
          this.$refs.readonlyModal.show();
        }, 250);
        return;
      }

      // if read/write user but not enabled, show readonly confirmation
      if (workflow.enabled) {
        this.editorWorkflow = workflow;
        this.readonlyMessage = " is currently enabled and will be launched in read-only mode. To edit this workflow, please disable it first.";
        setTimeout(() => {
          this.$refs.readonlyModal.show();
        }, 250);
        return;
      }

      // passed all conditions, launch
      this.$emit("setPage", "workflowEditor", {
        title: workflow.name,
        readonly: false
      });
      Server.post("lockWorkflow", {workflow: workflow._id})
    },

    reloadWorkflows() {
      this.setSortedWorkflows();
      this.reload++;
    },

    // other methods

    resetModalWorkflow() {
      this.modalWorkflow = {
        name: "",
        notes: "",
        startDate: "",
        frequencyType: "",
        schedule: {
          days: [false, false, false, false, false, false, false],
          startDate: new Moment(new Date()).format(),
          startTime: new Moment(new Date("1/1/1970 00:00:00.000")).format(),
          endTime: new Moment(new Date("1/1/1970 23:59:59.999")).format(),
          frequency: 60
        },
        logLevel: 3
      };
    },

    setWorkspaceOptions() {
      this.workspaceOptions = [];
      for (var i = 0; i < this.workspaces.length; i++) {
        var workspace = this.workspaces[i];
        this.workspaceOptions.push({
          value: workspace._id,
          text: workspace.name
        });
      }

      // select from userParms or first option if none selected
      if (this.workspaces.length > 0 && !this.workspace) {
        if (this.user.parms.workspace)
          var workspace = this.workspaces.find(
            a => a._id == this.user.parms.workspace
          );
        else {
          var workspace = this.workspaces[0];
        }
        this.$store.dispatch("wsStore/setWorkspace", { workspace: workspace });
      }
    },

    setSortedWorkflows() {
      this.sortedWorkflows = [];
      if (this.workflows.length == 0) {
        return;
      }

      var remaining = JSON.parse(JSON.stringify(this.workflows));
      // sort by user parm
      if (this.user.parms.workspaces) {
        var workspace = this.user.parms.workspaces.find(a => a.workspace == this.workspace);
        if(workspace) {
          var order = workspace.workflows;
          if (order) {
            for (var i = 0; i < order.length; i++) {
              var workflow = remaining.find(a => a._id == order[i]);
              if (workflow) {
                this.sortedWorkflows.push(workflow);
                remaining = Util.removeFromArrayByKey(
                  remaining,
                  "_id",
                  workflow._id
                );
              }
            }
          }
        }
      }

      // add remaining
      for (var i = 0; i < remaining.length; i++) {
        this.sortedWorkflows.unshift(remaining[i]);
      }
    },

    setFilteredWorkflows() {
      // @hack This is working for now. But I think it needs more
      // as well as to have some extra checks.
      this.reloadWorkflows()
      let workflows = this.sortedWorkflows;
      if (this.searchString === "") {
        this.isSearching = false
        return;
      }
      if (this.searchString) {
        this.isSearching = true
        workflows = workflows.filter(wf => {
          return (
            wf.name.toUpperCase().indexOf(this.searchString.toUpperCase()) > -1
          );
        });
      }

      this.sortedWorkflows = workflows;
    },

    saveWorkflowOrder() {
      if (!this.user.parms.workspaces)
        this.user.parms.workspaces = [];

      if (this.workspace != "" && !!this.workspace) {
        var workspace = this.user.parms.workspaces.find(a => a.workspace == this.workspace);
        if(!workspace) {
          workspace = {
            workspace: this.workspace,
            workflows: [],
            connections: []
          };
          this.user.parms.workspaces.push(workspace);
        }

        workspace.workflows = []; // clear order
        for (var i = 0; i < this.sortedWorkflows.length; i++) {
          var workflow = this.sortedWorkflows[i];
          workspace.workflows.push(
            workflow._id
          );
        }

        this.$store.dispatch("userStore/updateUserParms", { user: this.user });
      }
    }
  }
};
</script>






<style scoped lang="css">

#agLogPage-logs {
  position: relative;
  height: calc(100vh - 150px);
  top: 65px;
  overflow: auto;
  text-align: left;
}
#agLogPage-filterButton, #agLogPage-refreshButton, #agLogPage-deleteButton {
  font-size: 24px !important;
}

#agWfPage-wfs {
  position: relative;
  height: calc(100vh - 150px);
  top: 65px;
  overflow: auto;
}

#agWfPage-loadingSpinner {
  position: absolute;
  width: 50px;
  height: 50px;
  left: 50%;
  top: 50%;
  margin-left: -20px;
  margin-top: -20px;
}

#agWfPage-noWss {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -277px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agWfPage-noWss svg {
  font-size: 60px;
}

#agWfPage-noWfs {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -263.5px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agWfPage-noWfs svg {
  font-size: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/workflow/WorkflowsPage.vue